import * as React from 'react';
import { Form, DatePickerRange, Select, ClearFormButton, CustomSubmitButton, ButtonType, CustomSubmitButtonType, TextInput, CustomButton } from 'soprasteria-ui-library';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { ICareProvider } from '../../../models/ICareProvider';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { TreatmentsDropdown } from '../TreatmentReport/TreatmentsDropdown';
import { IPatientTreatment, IPatientTreatmentSearchParams } from '../../../models/IPatientTreatment';
import { useAuth } from '../../../App';
import { PatientTreatmentSearchApi } from '../../../api/PatientTreatmentSearchApi';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { VÅRDANSVARIG, LIBRETTOADMIN } from '../../../utils/RoleConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

interface IPatientTreatmentSearchForm {
    preSetSearchParams?: IPatientTreatmentSearchParams;
    searchPatientTreatments: (searchParams: IPatientTreatmentSearchParams) => void;
    searchPatientTreatmentsToExcel: (searchParams: IPatientTreatmentSearchParams) => void;
    disableExportToExcel: boolean;
}

export const PatientTreatmentSearchForm: React.FunctionComponent<IPatientTreatmentSearchForm> = ({ searchPatientTreatments, searchPatientTreatmentsToExcel, preSetSearchParams, disableExportToExcel }) => {

    const auth = useAuth();
    const api = new PatientTreatmentSearchApi(auth);
    const abortController = new AbortController();
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);
    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>();
    const [selectedUmbrellaOrganisationId, setSelectedUmbrellaOrganisationId] = React.useState<number>();
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);
    const [isCareProviderSelected, setIsCareProviderSelected] = React.useState<boolean>(false);
    const [treatmentCodes, setTreatmentCodes] = React.useState<IPatientTreatment[]>();
    const [treatmentTypes, setTreatmentTypes] = React.useState<IListItem[]>();
    const [treatmentStatuses, setTreatmentStatuses] = React.useState<IListItem[]>();
    const userInfo = React.useContext(UserContext);
    const hasRoleSystemAdmin = isInRole(LIBRETTOADMIN, userInfo.roles);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const firstDayLastYear = new Date(new Date().getFullYear() - 1, 0, 1);

    React.useEffect(() => {
        getUmbrellaOrganisations();
        getTreatmentCodes();
        getTreatmentTypes();
        getTreatmentStatuses();

        return () => {
            abortController.abort();
        }
    }, [])

    const getUmbrellaOrganisations = () => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaCareProviderIdToUse;
            if (preSetSearchParams?.umbrellaCareProviderId) {
                umbrellaCareProviderIdToUse = parseInt(preSetSearchParams.umbrellaCareProviderId.toString());
                setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse)
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }
            else if (!hasRoleSystemAdmin) {
                umbrellaCareProviderIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }

            setUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingCareProviders(true);

        api.getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                setCareProviders(undefined);
            }
            setIsFetchingCareProviders(false);
        }, undefined, () => { });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (hasRoleSystemAdmin) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider));
                setSelectedUmbrellaOrganisationId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const onCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (selectedCareProvider) {
            setIsCareProviderSelected(true);
        } else {
            setIsCareProviderSelected(false);
        }
    }

    const getTreatmentCodes = () => {
        api.getTreatmentCodes((data) => setTreatmentCodes(data));
    }

    const getTreatmentTypes = () => {
        api.getTreatmentTypes((data) => {
            var listItems = data.map<IListItem>((d) => { return { text: d.description, value: d.treatmentTypeId.toString() } });
            setTreatmentTypes(listItems);
        });
    }

    const getTreatmentStatuses = () => {
        api.getTreatmentStatuses((data) => {
            var listItems = data.map<IListItem>((d) => { return { text: d.description, value: d.patientTreatmentStatusId.toString() } });
            setTreatmentStatuses(listItems)
        });
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (umbrellaOrganisations && preSetSearchParams?.umbrellaCareProviderId) {
            return preSetSearchParams.umbrellaCareProviderId.toString();
        }

        if (selectedUmbrellaOrganisationId) {
            return selectedUmbrellaOrganisationId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? careProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (careProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders && careProviders.length === 1 && careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    return (
        <>
            <Form>
                <div className="row">
                    <div className="col-6">
                        <CareProviderDropdown
                            name="umbrellaCareProviderId"
                            label="Vårdgivare"
                            isLoading={isFetchingUmbrellaOrganisations}
                            careProviders={umbrellaOrganisations}
                            onValueChange={onUmbrellaCareProviderChange}
                            defaultValue={getUmbrellaCareProviderDefaultValue()}
                            disabled={!hasRoleSystemAdmin}
                            useFixedListItemHeight
                            showOrgNum
                            placeholder="Välj vårdgivare"
                            isClearable
                        />
                        <CareProviderDropdown
                            name="careProviderId"
                            label="Mottagning"
                            isLoading={isFetchingCareProviders}
                            onValueChange={onCareProviderChange}
                            careProviders={careProviders ?? []}
                            defaultValue={getCareProviderDefaultValue()}
                            disabled={!(hasRoleParaply || hasRoleSystemAdmin)}
                            useFixedListItemHeight
                            showClinicId
                            isClearable
                            resetValue={resetCareProviderValue}
                        />
                        <DatePickerRange label="Behandlingsdatum" name="treatmentDateFrom" nameSecondary="treatmentDateTo" inlineLabel
                            value={preSetSearchParams?.treatmentDateFrom ? new Date(preSetSearchParams.treatmentDateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.treatmentDateTo ? new Date(preSetSearchParams.treatmentDateTo) : undefined} />
                        <DatePickerRange label="Skapad" name="regDateFrom" nameSecondary="regDateTo" inlineLabel
                            value={preSetSearchParams?.regDateFrom ? new Date(preSetSearchParams.regDateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.regDateTo ? new Date(preSetSearchParams.regDateTo) : undefined} />
                        <TextInput name="patientPersonalSecurityId" label="Personnummer" inlineLabel/>
                    </div>
                    <div className="col-6">
                        <TreatmentsDropdown label="Åtgärdskod" treatments={treatmentCodes ?? []} name="treatmentCodeIds" isMultiple isLoading={treatmentCodes === undefined} required={false}
                            defaultValue={preSetSearchParams?.treatmentCodeIds?.map((code) => { return code.toString() })} />
                        <Select placeholder="Välj ärendetyp" label="Ärendetyp" name="treatmentTypeId" options={treatmentTypes ?? []} isLoading={treatmentTypes === undefined} inlineLabel isClearable selectedValue={preSetSearchParams?.treatmentTypeId?.toString()} />
                        <Select placeholder="Välj status" label="Status" name="statusId" options={treatmentStatuses ?? []} isLoading={treatmentStatuses === undefined} inlineLabel isClearable selectedValue={preSetSearchParams?.statusId?.toString()} />
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton onButtonSubmit={searchPatientTreatments}>Sök</CustomSubmitButton>
                            </span>
                            {
                                disableExportToExcel
                                ? <CustomButton disabled={disableExportToExcel} title="Det går inte att exportera till Excel då sökresultatet överstiger 100 0000 rader" buttonType={ButtonType.excel} buttonText="Exportera till Excel" />
                                : <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={searchPatientTreatmentsToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till Excel</CustomSubmitButton>
                            }                            
                            <ClearFormButton />
                            <CustomSubmitButton onButtonSubmit={searchPatientTreatments}>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </>
    )
}